import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Stage } from './Stage';
import { Ambient } from './Ambient';
import { VEXU_LOGO, VexuLockup } from './Brand';
import { SlideNav } from './SlideNav';
import { PresentationLogin } from './PresentationLogin';
import { useDocumentMeta } from '../../hooks/useDocumentMeta';
import { PARTNERS, PartnerId, PartnerProvider } from '../../contexts/PartnerContext';
import { CoverSlide } from './slides/CoverSlide';
import { AboutSlide } from './slides/AboutSlide';
import { TechnologySlide } from './slides/TechnologySlide';
import { VerticalsSlide } from './slides/VerticalsSlide';
import { PartnershipSlide } from './slides/PartnershipSlide';
import { ClosingSlide } from './slides/ClosingSlide';
import { TalabShiftSlide } from './talab/TalabShiftSlide';
import { TalabMeetSlide } from './talab/TalabMeetSlide';
import { TalabMarketplaceSlide } from './talab/TalabMarketplaceSlide';
const EASE = [0.23, 1, 0.32, 1] as const;
const VERTICALS_INDEX = 3;
const AUTH_KEY = 'vexu-presentation-access';
const TALAB_TITLES = ['The Shift to Conversational Commerce', 'Meet Talab', 'Storefront + Marketplace'];
interface PresentationRootProps {
  /** Renders the full interactive Vexu AI mobile experience on demand. */
  renderVexuApp: () => React.ReactNode;
}
export function PresentationRoot({
  renderVexuApp
}: PresentationRootProps) {
  const [index, setIndex] = useState(0);
  const [appOpen, setAppOpen] = useState(false);
  const [talabIndex, setTalabIndex] = useState<number | null>(null);
  const [partnerId, setPartnerId] = useState<PartnerId | null>(() => {
    if (typeof window === 'undefined') return null;
    const stored = window.sessionStorage.getItem(AUTH_KEY);
    return stored === 'zain' || stored === 'etisalat' ? stored : null;
  });
  const partner = PARTNERS[partnerId ?? 'zain'];
  const authenticated = partnerId !== null;
  useDocumentMeta({
    title: 'Vexu Presentation',
    description: 'Vexu AI — the AI conversational layer for telecom. One self-hosted AI technology stack powering Vexu AI, Vexu Portal and Talab.',
    iconUrl: VEXU_LOGO
  });
  const titles = ['Cover', 'About Vexu AI', 'The Technology Behind Vexu', 'Three Commercial Verticals', `Vexu AI × ${partner.name}`, 'Closing'];
  const goTo = useCallback((next: number) => {
    setIndex(Math.max(0, Math.min(titles.length - 1, next)));
  }, [titles.length]);
  const openVexuApp = useCallback(() => setAppOpen(true), []);
  const closeVexuApp = useCallback(() => setAppOpen(false), []);
  const openTalab = useCallback(() => setTalabIndex(0), []);
  const exitTalab = useCallback(() => {
    setTalabIndex(null);
    setIndex(VERTICALS_INDEX);
  }, []);
  useEffect(() => {
    if (!authenticated) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (appOpen) {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeVexuApp();
        }
        return;
      }
      if (talabIndex !== null) {
        switch (event.key) {
          case 'ArrowRight':
          case 'PageDown':
          case ' ':
            event.preventDefault();
            if (talabIndex < TALAB_TITLES.length - 1) {
              setTalabIndex(talabIndex + 1);
            } else {
              exitTalab();
            }
            break;
          case 'ArrowLeft':
          case 'PageUp':
            event.preventDefault();
            if (talabIndex > 0) setTalabIndex(talabIndex - 1);else exitTalab();
            break;
          case 'Escape':
            event.preventDefault();
            exitTalab();
            break;
          default:
            break;
        }
        return;
      }
      switch (event.key) {
        case 'ArrowRight':
        case 'PageDown':
        case ' ':
          event.preventDefault();
          setIndex(prev => Math.min(titles.length - 1, prev + 1));
          break;
        case 'ArrowLeft':
        case 'PageUp':
          event.preventDefault();
          setIndex(prev => Math.max(0, prev - 1));
          break;
        case 'Home':
          event.preventDefault();
          setIndex(0);
          break;
        case 'End':
          event.preventDefault();
          setIndex(titles.length - 1);
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [appOpen, authenticated, closeVexuApp, exitTalab, talabIndex, titles.length]);
  const grantAccess = useCallback((nextPartner: PartnerId) => {
    window.sessionStorage.setItem(AUTH_KEY, nextPartner);
    setPartnerId(nextPartner);
  }, []);
  const renderTalabSlide = () => {
    switch (talabIndex) {
      case 0:
        return <TalabShiftSlide onExit={exitTalab} data-id="element-493" data-mp="1sq2cqko8n5hq" />;
      case 1:
        return <TalabMeetSlide onExit={exitTalab} data-id="element-494" data-mp="1wqulzdx41ae0" />;
      default:
        return <TalabMarketplaceSlide onExit={exitTalab} data-id="element-495" data-mp="3h7lqfkclkxg1" />;
    }
  };
  const renderSlide = () => {
    switch (index) {
      case 0:
        return <CoverSlide data-id="element-496" data-mp="3d579x6wei9rx" />;
      case 1:
        return <AboutSlide data-id="element-497" data-mp="3d8mo87i93esa" />;
      case 2:
        return <TechnologySlide data-id="element-498" data-mp="386du37tn66ol" />;
      case VERTICALS_INDEX:
        return <VerticalsSlide onOpenVexuApp={openVexuApp} onOpenTalab={openTalab} data-id="element-499" data-mp="338c8af4tzt9f" />;
      case 4:
        return <PartnershipSlide data-id="element-500" data-mp="4p5e6tih5gwi" />;
      default:
        return <ClosingSlide data-id="element-501" data-mp="3vwrk6sso1vgj" />;
    }
  };
  if (!authenticated) {
    return <PresentationLogin onAuthenticated={grantAccess} data-id="element-502" data-mp="3vxjiwchk89b1" />;
  }
  return <PartnerProvider partner={partner} data-id="element-503" data-mp="3r4zu0jdf9rqx">
    <div className="relative h-full w-full" data-id="element-504" data-mp="3r3yi6col1185">
      <Stage data-id="element-505" data-mp="3m7gdfct952vt">
        <AnimatePresence mode="wait" data-id="element-506" data-mp="3m5x11xapvbzb">
          <motion.div key={talabIndex !== null ? `talab-${talabIndex}` : index} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.28,
            ease: EASE
          }} className="absolute inset-0" data-id="element-507" data-mp="3m4u87jtu2zgr">
            {talabIndex !== null ? renderTalabSlide() : renderSlide()}
          </motion.div>
        </AnimatePresence>

        {talabIndex !== null ? <SlideNav slides={TALAB_TITLES} current={talabIndex} onSelect={setTalabIndex} onPrev={() => talabIndex > 0 ? setTalabIndex(talabIndex - 1) : exitTalab()} onNext={() => talabIndex < TALAB_TITLES.length - 1 ? setTalabIndex(talabIndex + 1) : exitTalab()} edgeEnabled data-id="element-508" data-mp="2380dtbmewqoj" /> : <SlideNav slides={titles} current={index} onSelect={goTo} onPrev={() => goTo(index - 1)} onNext={() => goTo(index + 1)} data-id="element-509" data-mp="1p7zg251g1zda" />}
      </Stage>

      {/* Interactive Vexu AI application */}
      <AnimatePresence data-id="element-510" data-mp="3tqgpb3rpfb9s">
        {appOpen && <motion.div key="vexu-app" initial={{
          opacity: 0,
          scale: 0.985
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.99
        }} transition={{
          duration: 0.3,
          ease: EASE
        }} className="fixed inset-0 z-[200] overflow-hidden bg-white" data-id="element-511" data-mp="3tr8muk4v3j2p">
            <Ambient variant="cover" data-id="element-512" data-mp="1kjx9507i9r9" />

            <div className="relative z-10 h-full w-full" data-id="element-513" data-mp="gb15p8b9dzxn">{renderVexuApp()}</div>

            <div className="pointer-events-none absolute bottom-[28px] left-[30px] z-[210] flex items-center gap-[16px]" data-id="element-514" data-mp="bcg3j3cg566k">
              <VexuLockup size={24} wordmarkSize={15} data-id="element-515" data-mp="pcik8m90ptk8" />
              <span className="h-[12px] w-px bg-slate-300" data-id="element-516" data-mp="pa4oeb6ikdbm" />
              <span className="text-[10.5px] font-semibold uppercase tracking-[0.28em] text-slate-400" data-id="element-517" data-mp="lbyo9m06kvzo">
                Live Product Demonstration
              </span>
              <span className="rounded-full border border-vexu/35 bg-vexu/[0.07] px-[10px] py-[4px] text-[9px] font-semibold uppercase tracking-[0.18em] text-vexu-700" data-id="element-518" data-mp="2n2ihcvoc1o6q">
                Patent Pending
              </span>
            </div>

            <button type="button" onClick={closeVexuApp} className="fixed left-[26px] top-[24px] z-[210] flex items-center gap-[10px] rounded-full border border-slate-900/10 bg-white/90 px-[18px] py-[10px] text-[13px] font-medium text-slate-900 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.35)] backdrop-blur-xl transition-colors duration-200 ease-out hover:border-vexu/50 hover:bg-white" data-id="element-519" data-mp="3gkgvjgnvdb0x">
              <ArrowLeft className="h-[15px] w-[15px] text-vexu-600" data-id="element-520" data-mp="31or6taraujsi" />
              Back to Vexu Ecosystem
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>
    </PartnerProvider>;
}